import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { usersAPI } from '../../services/api';
import { User, UserRole, UserStatus } from '../../types';
import { useCompany } from '../CompanyContext';
import { useUser } from '../UserContext';
import { useTranslation } from 'react-i18next';

const EmployeesList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { selectedCompany } = useCompany();
  const { isAdmin } = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('all');

  useEffect(() => {
    if (selectedCompany || isAdmin) {
      loadData();
    }
  }, [selectedCompany, isAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      let usersData: User[];
      
      if (isAdmin) {
        // Admin can see all users across all companies
        usersData = await usersAPI.getAll();
      } else if (selectedCompany) {
        // Non-admin users can only see users from their selected company
        usersData = await usersAPI.getByCompany(selectedCompany.id);
      } else {
        usersData = [];
      }
      
      setUsers(usersData);
    } catch (err) {
      setError(t('employees.errorLoad') || '');
      console.error('Error loading employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('employees.delete') || '')) {
      try {
        await usersAPI.delete(id);
        setUsers(users.filter(user => user.id !== id));
      } catch (err) {
        setError(t('employees.delete') || '');
        console.error('Error deleting employee:', err);
      }
    }
  };

  const getRoleBadge = (role: UserRole) => {
    const roleConfig = {
      [UserRole.ADMIN]: 'bg-red-100 text-red-800',
      [UserRole.OWNER]: 'bg-purple-100 text-purple-800',
      [UserRole.BRANCH_MANAGER]: 'bg-blue-100 text-blue-800',
      [UserRole.EMPLOYEE]: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleConfig[role]}`}>
        {role.replace('_', ' ')}
      </span>
    );
  };

  const getStatusBadge = (status: UserStatus) => {
    const statusConfig = {
      [UserStatus.ACTIVE]: 'bg-green-100 text-green-800',
      [UserStatus.INACTIVE]: 'bg-gray-100 text-gray-800',
      [UserStatus.SUSPENDED]: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig[status]}`}>
        {status}
      </span>
    );
  };

  // Filter users based on selected filters
  const filteredUsers = users.filter(user => {
    const roleMatch = selectedRole === 'all' || user.role === selectedRole;
    return roleMatch;
  });

  if (!selectedCompany && !isAdmin) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('users.noSelectedCompanyTitle')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('users.noSelectedCompanyText')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('employees.title')}</h1>
          <p className="text-gray-600">
            {isAdmin 
              ? t('employees.subtitleAll')
              : t('employees.subtitleCompany', { company: selectedCompany?.name })
            }
          </p>
        </div>
        <Link
          to="/users/new"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t('employees.add')}
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-700">{t('employees.filters')}</span>
          </div>

          <div>
            <label htmlFor="role-filter" className="block text-sm font-medium text-gray-700">
              {t('employees.role')}
            </label>
            <select
              id="role-filter"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="all">{t('employees.allRoles')}</option>
              <option value={UserRole.EMPLOYEE}>{t('employees.employee')}</option>
              <option value={UserRole.BRANCH_MANAGER}>{t('employees.branchManager')}</option>
              <option value={UserRole.OWNER}>{t('employees.owner')}</option>
              {isAdmin && <option value={UserRole.ADMIN}>{t('employees.admin')}</option>}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredUsers.map((user) => (
            <li key={user.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-600 font-medium">
                          {user.firstName.charAt(0).toUpperCase()}{user.lastName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </p>
                        <div>{getRoleBadge(user.role)}</div>
                        <div>{getStatusBadge(user.status)}</div>
                      </div>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      {user.phone && (
                        <p className="text-sm text-gray-500">{user.phone}</p>
                      )}
                      {isAdmin && user.company && (
                        <p className="text-sm text-gray-500">{user.company.name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/users/${user.id}`}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      to={`/users/${user.id}/edit`}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                {user.lastLoginAt && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500">
                      {t('employees.lastLogin', { date: new Date(user.lastLoginAt).toLocaleDateString(i18n.language || 'en-US') })}
                    </p>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {isAdmin 
              ? t('employees.noneAll')
              : t('employees.noneCompany', { company: selectedCompany?.name })
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default EmployeesList;
